import { makeStyles } from "@fluentui/react-components";
import { projectIqTokens } from "apps/project-iq/theme/projectIqTokens";
import { designTokens } from "theme/customTheme";

const CARD_PADDING = designTokens.spacing[6];
const GRID_COL_GAP = designTokens.spacing[10];
const GRID_ROW_GAP = designTokens.spacing[5];
const HEADER_FONT_SIZE = designTokens.fontSize.sm;
const LABEL_FONT_SIZE = designTokens.fontSize.xxs;
const LABEL_LETTER_SPACING = designTokens.letterSpacing.wide;

const SKELETON_CARD_TITLE_WIDTH = "140px";
const SKELETON_LABEL_WIDTH = "60px";
const SKELETON_LABEL_MARGIN_BOTTOM = "6px";
const SKELETON_VALUE_WIDTH = "120px";
const SKELETON_DESC_LABEL_WIDTH = "80px";
const SKELETON_TEMPLATE_TITLE_WIDTH = "220px";
const SKELETON_TEMPLATE_BTN_PRIMARY_WIDTH = "110px";
const SKELETON_TEMPLATE_BTN_SECONDARY_WIDTH = "80px";

export const useAnalyticsOverviewTabStyles = makeStyles({
  root: {
    paddingBottom: designTokens.spacing[6],
  },

  card: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    padding: CARD_PADDING,
    fontFamily: projectIqTokens.fontFamily,
    boxShadow: projectIqTokens.shadowSm,
  },

  cardHeaderRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: designTokens.spacing[3],
  },

  cardHeader: {
    textTransform: "uppercase",
    fontSize: HEADER_FONT_SIZE,
    fontWeight: designTokens.fontWeight.bold,
    color: projectIqTokens.ink,
    margin: "0",
    paddingTop: "0",
    paddingBottom: "0",
    paddingLeft: "0",
    paddingRight: "0",
  },

  divider: {
    borderTop: `1px solid ${projectIqTokens.line}`,
    borderRight: "none",
    borderBottom: "none",
    borderLeft: "none",
    marginTop: designTokens.spacing[4],
    marginBottom: designTokens.spacing[4],
    marginLeft: "0",
    marginRight: "0",
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    columnGap: GRID_COL_GAP,
    rowGap: GRID_ROW_GAP,
    marginTop: "0",
    marginBottom: "0",
    marginLeft: "0",
    marginRight: "0",
    paddingLeft: "0",
    "@media (max-width: 480px)": {
      gridTemplateColumns: "1fr",
    },
  },

  metaLabel: {
    display: "block",
    textTransform: "uppercase",
    fontSize: LABEL_FONT_SIZE,
    letterSpacing: LABEL_LETTER_SPACING,
    color: projectIqTokens.muted,
    fontWeight: designTokens.fontWeight.semibold,
  },

  metaValue: {
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.ink,
    fontWeight: designTokens.fontWeight.normal,
    marginTop: designTokens.spacing[1],
    marginLeft: "0",
    marginRight: "0",
    paddingLeft: "0",
    display: "block",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },

  gridCellFullWidth: {
    gridColumn: "1 / -1",
  },

  metaValueEmpty: {
    fontStyle: "italic",
    color: projectIqTokens.muted,
    fontSize: designTokens.fontSize.sm,
    marginTop: designTokens.spacing[1],
    marginLeft: "0",
    marginRight: "0",
    paddingLeft: "0",
    display: "block",
    wordBreak: "break-word",
    overflowWrap: "break-word",
  },

  templateCard: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    padding: CARD_PADDING,
    fontFamily: projectIqTokens.fontFamily,
    marginTop: designTokens.spacing[4],
  },

  templateCardHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: designTokens.spacing[2],
    marginBottom: designTokens.spacing[3],
  },

  templateCardDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: designTokens.accent[500],
  },

  templateCardMeta: {
    fontSize: designTokens.fontSize.xxs,
    fontWeight: designTokens.fontWeight.semibold,
    textTransform: "uppercase" as const,
    letterSpacing: designTokens.letterSpacing.wide,
    color: projectIqTokens.muted,
  },

  templateCardDesc: {
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.ink,
    lineHeight: "1.6",
    marginTop: "0",
    marginBottom: designTokens.spacing[3],
    marginLeft: "0",
    marginRight: "0",
  },

  templateCardWhenToUse: {
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.muted,
    marginTop: "0",
    marginBottom: designTokens.spacing[4],
    marginLeft: "0",
    marginRight: "0",
  },

  templateCardWhenToUseLabel: {
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink,
  },

  templateCardActions: {
    display: "flex",
    flexDirection: "row",
    gap: designTokens.spacing[2],
    flexWrap: "wrap",
  },

  templateCardLink: {
    display: "inline-flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    paddingTop: designTokens.spacing[1],
    paddingBottom: designTokens.spacing[1],
    paddingLeft: designTokens.spacing[3],
    paddingRight: designTokens.spacing[3],
    borderRadius: designTokens.borderRadius.md,
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.medium,
    textDecoration: "none",
    background: "transparent",
    cursor: "pointer",
  },

  templateCardLinkPrimary: {
    border: `1px solid ${designTokens.accent[500]}`,
    color: designTokens.accent[500],
  },

  templateCardLinkSecondary: {
    border: `1px solid ${projectIqTokens.muted}`,
    color: projectIqTokens.muted,
  },

  // Attachment panel styles
  attachmentPanel: {
    backgroundColor: projectIqTokens.card,
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.lg,
    padding: CARD_PADDING,
    fontFamily: projectIqTokens.fontFamily,
    marginTop: designTokens.spacing[4],
  },

  attachmentList: {
    listStyleType: "none",
    padding: "0",
    margin: "0",
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
  },

  attachmentRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: designTokens.spacing[3],
    padding: designTokens.spacing[3],
    border: `1px solid ${projectIqTokens.line}`,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: projectIqTokens.bg,
  },

  attachmentBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: projectIqTokens.tealSoft,
    color: projectIqTokens.teal,
    fontSize: designTokens.fontSize.xs,
    fontWeight: designTokens.fontWeight.bold,
    flexShrink: 0,
    textTransform: "uppercase",
  },

  attachmentInfo: {
    flex: 1,
    minWidth: 0,
  },

  attachmentName: {
    fontSize: designTokens.fontSize.sm,
    fontWeight: designTokens.fontWeight.semibold,
    color: projectIqTokens.ink,
    margin: "0",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  attachmentMeta: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.muted,
    margin: "0",
    marginTop: designTokens.spacing[1],
  },

  attachmentActions: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[1],
    flexShrink: 0,
  },

  attachmentEmptyText: {
    fontSize: designTokens.fontSize.sm,
    color: projectIqTokens.muted,
    fontStyle: "italic",
    margin: "0",
    marginTop: designTokens.spacing[3],
  },

  // Skeleton classes (mirrored from DiagnosticOverviewTab)
  skeletonCardTitle: {
    width: SKELETON_CARD_TITLE_WIDTH,
  },

  skeletonLabel: {
    width: SKELETON_LABEL_WIDTH,
    marginBottom: SKELETON_LABEL_MARGIN_BOTTOM,
  },

  skeletonValue: {
    width: SKELETON_VALUE_WIDTH,
  },

  skeletonDescriptionLabel: {
    width: SKELETON_DESC_LABEL_WIDTH,
    marginBottom: SKELETON_LABEL_MARGIN_BOTTOM,
  },

  skeletonDescriptionValue: {
    width: "100%",
  },

  skeletonTemplateTitle: {
    width: SKELETON_TEMPLATE_TITLE_WIDTH,
    marginBottom: designTokens.spacing[3],
  },

  skeletonTemplateDescLine: {
    width: "100%",
    marginBottom: designTokens.spacing[2],
  },

  skeletonTemplateDescLineShort: {
    width: "75%",
    marginBottom: designTokens.spacing[4],
  },

  skeletonTemplateActions: {
    display: "flex",
    gap: designTokens.spacing[2],
  },

  skeletonTemplatePrimaryButton: {
    width: SKELETON_TEMPLATE_BTN_PRIMARY_WIDTH,
    borderRadius: designTokens.borderRadius.md,
  },

  skeletonTemplateSecondaryButton: {
    width: SKELETON_TEMPLATE_BTN_SECONDARY_WIDTH,
    borderRadius: designTokens.borderRadius.md,
  },

  attachmentEditRow: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    marginTop: designTokens.spacing[1],
    flexWrap: "nowrap",
  },

  attachmentEditSelect: {
    maxWidth: "220px",
    minWidth: "120px",
    flex: "1 1 auto",
  },

  attachmentEditError: {
    fontSize: designTokens.fontSize.xs,
    color: projectIqTokens.rust,
    marginTop: designTokens.spacing[1],
    display: "block",
  },
});
